import { type SxProps, type Theme } from "@mui/material/styles";

import Icons from "assets";
import { Box } from "ui/box";
import { Checkbox } from "ui/checkbox";
import { Panel } from "ui/panel";
import { Popover } from "ui/popover";
import { TextButton } from "ui/text-button";

import { useOpen } from "hooks/use-open";

import { ColumnOptionsProps } from "./column-options.types";

const OPEN_TRIGGER_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.primary.dark });

/**
 * Figma's checkbox rows are 32px: the 20px box inside its 6px `container/p`. The theme negates that
 * padding so the halo costs no layout, so it comes back here — 6px inside the panel's 12px, and rows
 * 12px apart.
 */
const OPTION_LIST_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  p: 0.75,
};

const ColumnOptions = ({
  tableConfigKey,
  tableColumnsConfig,
  columnOptions,
  onToggleColumn,
}: ColumnOptionsProps) => {
  const { open, handleClose, handleToggle } = useOpen(false);

  const hiddenColumnIds = new Set(tableColumnsConfig[tableConfigKey]?.hidden ?? []);

  return (
    <Popover
      padding={0}
      placement="bottom-start"
      width={320}
      open={open}
      onClose={handleClose}
      triggerElement={
        <TextButton
          startIcon={<Icons.Settings />}
          data-testid="column-options-button"
          sx={open ? OPEN_TRIGGER_SX : undefined}
          onClick={handleToggle}>
          Column options
        </TextButton>
      }>
      <Panel title="Manage columns">
        <Box sx={OPTION_LIST_SX}>
          {columnOptions.map((option) => {
            const isVisible = !hiddenColumnIds.has(option.id);

            return (
              <Checkbox
                key={option.id}
                size="sm"
                label={option.label}
                checked={isVisible}
                onChange={() => onToggleColumn(option.id)}
              />
            );
          })}
        </Box>
      </Panel>
    </Popover>
  );
};

export default ColumnOptions;
